<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const props = withDefaults(
  defineProps<{
    open: boolean
    target: 'vhq' | 'documents'
    profile: {
      name: string
      first_name: string
      last_name: string
      ico: string
      dic: string
      ic_dph: string
    }
    address: {
      street: string
      street_number: string
      city: string
      psc: string
      country: string
    }
    editing: boolean
    saving?: boolean
  }>(),
  { saving: false }
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'submit', value: { profile: typeof props.profile; address: typeof props.address }): void
}>()

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val),
})

const form = reactive({
  profile: { ...props.profile },
  address: { ...props.address },
})

watch(
  () => props.open,
  (open) => {
    if (!open) return
    form.profile = { ...props.profile }
    form.address = { ...props.address }
  }
)

function onSubmit() {
  emit('submit', { profile: form.profile, address: form.address })
  emit('update:open', false)
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-w-2xl">
      <DialogHeader>
        <DialogTitle>
          {{ editing ? 'Upraviť fakturačný profil' : 'Nový fakturačný profil' }}
        </DialogTitle>
      </DialogHeader>
      <div class="px-4 py-5 sm:p-6">
        <div class="flex flex-col gap-4 pt-4">
          <div class="flex flex-row gap-4">
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Meno / Názov
              </label>
              <FormKit
                type="text"
                v-model="form.profile.name"
                name="bp_name"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
          </div>
          <div class="flex flex-row gap-4">
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Meno
              </label>
              <FormKit
                type="text"
                v-model="form.profile.first_name"
                name="bp_first_name"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Priezvisko
              </label>
              <FormKit
                type="text"
                v-model="form.profile.last_name"
                name="bp_last_name"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
          </div>
          <div class="flex flex-row gap-4">
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                IČO
              </label>
              <FormKit
                type="text"
                v-model="form.profile.ico"
                name="bp_ico"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                DIČ
              </label>
              <FormKit
                type="text"
                v-model="form.profile.dic"
                name="bp_dic"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                IČ DPH
              </label>
              <FormKit
                type="text"
                v-model="form.profile.ic_dph"
                name="bp_ic_dph"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
          </div>
          <div class="flex flex-row gap-4">
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ulica *
              </label>
              <FormKit
                type="text"
                v-model="form.address.street"
                name="bp_street"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Číslo
              </label>
              <FormKit
                type="text"
                v-model="form.address.street_number"
                name="bp_street_number"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
          </div>
          <div class="flex flex-row gap-4">
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Mesto *
              </label>
              <FormKit
                type="text"
                v-model="form.address.city"
                name="bp_city"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                PSČ *
              </label>
              <FormKit
                type="text"
                v-model="form.address.psc"
                name="bp_psc"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
            <div class="flex flex-col w-full min-w-0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Krajina *
              </label>
              <FormKit
                type="text"
                v-model="form.address.country"
                name="bp_country"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row px-4 py-3 justify-between bg-gray-50 text-right sm:px-6 dark:bg-white/3">
        <Button
          variant="outline"
          @click="emit('update:open', false)"
          class="focus:outline-none text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
        >
          Zrušiť
        </Button>
        <Button
          type="button"
          @click="onSubmit"
          :disabled="saving"
          class="focus:outline-none text-white bg-brand-600 hover:bg-brand-700 focus:ring-4 focus:ring-brand-500 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2"
        >
          {{ editing ? 'Uložiť zmeny' : 'Uložiť profil' }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
